import { useMutation } from '@tanstack/react-query';
import { LoadedUser } from '../../interfaces';
import { invalidateWaivers, publishWaiver } from '../../services/waivers';
import { GetWaiversData, PublishWaiversResponse } from '../../types/waivers';
import useUser from '../global/useUser';
import useGetOnboarding from '../businesses/useGetOnboarding';

interface PublishWaiverOptions {
  autoFetch?: boolean;
  draft: GetWaiversData | null | undefined;
  disabled?: boolean;
  onSuccess?: (waiver: PublishWaiversResponse | null) => void | Promise<void>;
  onError?: () => void | Promise<void>;
}

function usePublishWaiver({
  draft,
  disabled,
  onSuccess,
  onError,
  autoFetch = true,
}: PublishWaiverOptions) {
  const { user } = useUser<LoadedUser>();
  const { reloadAfterUpdate } = useGetOnboarding();

  const businessId = user?.business?.id;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!draft || disabled || !businessId) return null;

      const response = await publishWaiver(draft.id, businessId);

      if (response && autoFetch) {
        reloadAfterUpdate('WAIVERS');
        await invalidateWaivers();
      }

      return response;
    },
    onSuccess,
    onError,
  });

  return {
    mutation,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export default usePublishWaiver;
